import { z } from 'zod';

import { MAX_PROFILE_BIO_LENGTH } from '@/src/features/profile/profile-limits';
import { usernameValidationMessage } from '@/src/features/profile/username';
import { isAtLeastAge, normalizeDateOfBirthInput } from '@/src/utils/age';

const dateOfBirthSchema = z
  .string()
  .trim()
  .transform((value, context) => {
    const normalized = normalizeDateOfBirthInput(value);

    if (!normalized.ok) {
      context.addIssue({
        code: 'custom',
        message: normalized.message,
      });
      return z.NEVER;
    }

    return normalized.value;
  });

const optionalOnboardingEmailSchema = z
  .string()
  .trim()
  .email('Enter a valid email address.')
  .optional()
  .or(z.literal(''))
  .transform((value) => (value ? value : undefined));

export const signInSchema = z.object({
  email: z.string().trim().email('Enter a valid email address.'),
  password: z.string().min(1, 'Password is required.'),
});

export const signUpSchema = z.object({
  fullName: z
    .string()
    .trim()
    .min(2, 'Enter your full name.')
    .max(80, 'Full name must be 80 characters or fewer.'),
  dateOfBirth: dateOfBirthSchema.refine(
    (dateOfBirth) => isAtLeastAge(dateOfBirth),
    'You must be at least 13 years old to create a Frienzzi account.',
  ),
  email: z.string().trim().email('Enter a valid email address.'),
  password: z.string().min(8, 'Password must be at least 8 characters.'),
  acceptedLegal: z
    .boolean()
    .refine(
      (accepted) => accepted,
      'You must agree to the Terms of Service and Privacy Policy.',
    ),
});

export const genderSchema = z.object({
  value: z.enum(['female', 'male'], {
    error: 'Choose male or female.',
  }),
  customText: z.null(),
});

export const profileSchema = z.object({
  fullName: z
    .string()
    .trim()
    .min(2, 'Enter your full name.')
    .max(80, 'Full name must be 80 characters or fewer.'),
  bio: z
    .string()
    .trim()
    .max(
      MAX_PROFILE_BIO_LENGTH,
      `Bio must be ${MAX_PROFILE_BIO_LENGTH} characters or fewer.`,
    )
    .nullable(),
  gender: genderSchema,
  location: z
    .string()
    .trim()
    .max(120, 'Location must be 120 characters or fewer.')
    .nullable(),
});

export const emailChangeSchema = z.object({
  currentPassword: z.string().min(1, 'Enter your current password.'),
  newEmail: z.string().trim().email('Enter a valid email address.'),
});

export const onboardingSchema = z.object({
  bio: z
    .string()
    .trim()
    .max(
      MAX_PROFILE_BIO_LENGTH,
      `Bio must be ${MAX_PROFILE_BIO_LENGTH} characters or fewer.`,
    )
    .nullable()
    .optional()
    .transform((value) => (value ? value : null)),
  email: optionalOnboardingEmailSchema,
  fullName: z
    .string()
    .trim()
    .min(2, 'Enter your full name.')
    .max(80, 'Full name must be 80 characters or fewer.'),
  username: z
    .string()
    .trim()
    .superRefine((value, context) => {
      const message = usernameValidationMessage(value);

      if (message) {
        context.addIssue({
          code: 'custom',
          message,
        });
      }
    }),
  dateOfBirth: dateOfBirthSchema,
  gender: genderSchema,
  location: z
    .string()
    .trim()
    .max(120, 'Location must be 120 characters or fewer.')
    .nullable(),
  password: z.string().optional(),
});

export type SignInValues = z.infer<typeof signInSchema>;
export type SignUpValues = z.infer<typeof signUpSchema>;
export type ProfileValues = z.infer<typeof profileSchema>;
export type EmailChangeValues = z.infer<typeof emailChangeSchema>;
export type GenderValues = z.infer<typeof genderSchema>;
export type OnboardingValues = z.infer<typeof onboardingSchema>;
